/* eslint-disable react-refresh/only-export-components */
import { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { authApi, clearAccessToken } from '../services/api'
import { ROLE_HIERARCHY } from '../constants/roles'
import { getStoredCurrentOrg } from '../lib/currentOrgStorage'

export const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [organizations, setOrganizations] = useState([])
  const [commandCenterAccess, setCommandCenterAccess] = useState(false)
  const [authConfig, setAuthConfig] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const authRequestVersion = useRef(0)
  const profileRequestVersion = useRef(0)

  useEffect(() => {
    const initAuth = async () => {
      const version = authRequestVersion.current
      try {
        const session = await authApi.refresh()
        if (authRequestVersion.current !== version) {
          return
        }
        if (session?.access_token) {
          setUser(session.user)
          setOrganizations(session.organizations || [])
          setCommandCenterAccess(Boolean(session.command_center_access))
          setAuthConfig(session.auth_config || null)
        }
      } catch {
        if (authRequestVersion.current !== version) {
          return
        }
        clearAccessToken()
        localStorage.removeItem('current_org')
        setUser(null)
        setOrganizations([])
        setCommandCenterAccess(false)
        setAuthConfig(null)
      } finally {
        if (authRequestVersion.current === version) {
          setLoading(false)
        }
      }
    }
    
    initAuth()
  }, [])

  const applySession = useCallback((response) => {
    setUser(response.user)
    setOrganizations(response.organizations || [])
    setCommandCenterAccess(Boolean(response.command_center_access))
    setAuthConfig(response.auth_config || null)
    setLoading(false)

    if (response.organizations?.length > 0) {
      const defaultOrg = response.organizations[0]
      localStorage.setItem('current_org', JSON.stringify(defaultOrg))
    }
  }, [])

  const login = useCallback(async (email, password, totpCode = null, authFlowId = null) => {
    authRequestVersion.current += 1
    setError(null)
    try {
      const response = await authApi.login(email, password, totpCode, authFlowId)

      if (response.requires_mfa_setup) {
        return {
          success: false,
          requiresMFASetup: true,
          authFlowId: response.auth_flow_id || null,
        }
      }

      if (response.requires_mfa) {
        return {
          success: false,
          requiresMFA: true,
          email,
          password,
          authFlowId: response.auth_flow_id || null,
          mfaMethods: response.mfa_methods || []
        }
      }
      
      applySession(response)
      
      return {
        success: true,
        requiresMFA: false,
        user: response.user,
        organizations: response.organizations
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Login failed'
      setError(errorMessage)
      setLoading(false)
      throw new Error(errorMessage)
    }
  }, [applySession])

  const logout = useCallback(async () => {
    authRequestVersion.current += 1
    try {
      await authApi.logout()
    } catch (err) {
      console.error('Logout error:', err)
    } finally {
      setUser(null)
      setOrganizations([])
      setCommandCenterAccess(false)
      setAuthConfig(null)
      setLoading(false)
      localStorage.removeItem('current_org')
    }
  }, [])

  const setupMFA = useCallback(async (enable = true) => {
    try {
      const response = await authApi.setupMFA(enable)
      return response
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to setup MFA'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [])

  const startMFAEnrollment = useCallback(async (authFlowId) => {
    try {
      return await authApi.startMFAEnrollment(authFlowId)
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to start MFA setup'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [])

  const verifyMFAEnrollment = useCallback(async (authFlowId, code) => {
    try {
      const response = await authApi.verifyMFAEnrollment(authFlowId, code)
      applySession(response)
      return { success: true }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Invalid MFA code'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [applySession])

  const verifyMFA = useCallback(async (code, email = null, password = null, authFlowId = null) => {
    try {
      if (email && password) {
        return await login(email, password, code, authFlowId)
      } else {
        const response = await authApi.verifyMFA(code)
        return response
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Invalid MFA code'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [login])

  const forgotPassword = useCallback(async (email) => {
    try {
      return await authApi.forgotPassword(email)
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to send password reset email'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [])

  const resetPassword = useCallback(async (userId, code, newPassword) => {
    try {
      return await authApi.resetPassword(userId, code, newPassword)
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to reset password'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [])

  const acceptInvitation = useCallback(async (userId, code, newPassword) => {
    try {
      return await authApi.acceptInvitation(userId, code, newPassword)
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to accept invitation'
      setError(errorMessage)
      throw new Error(errorMessage)
    }
  }, [])

  const refreshProfile = useCallback(async () => {
    const profileVersion = profileRequestVersion.current + 1
    profileRequestVersion.current = profileVersion
    const authVersion = authRequestVersion.current
    try {
      const profile = await authApi.getProfile()
      if (
        profileRequestVersion.current === profileVersion
        && authRequestVersion.current === authVersion
      ) {
        setUser(profile.user)
        setOrganizations(profile.organizations || [])
        setCommandCenterAccess(Boolean(profile.command_center_access))
        setAuthConfig(profile.auth_config || null)
      }
      return profile
    } catch (err) {
      console.error('Failed to refresh profile:', err)
      throw err
    }
  }, [])

  const hasRole = useCallback((requiredRole, orgId = null) => {
    if (!user || !organizations.length) return false
    
    const org = orgId 
      ? organizations.find(o => o.id === orgId)
      : getStoredCurrentOrg()
    
    if (!org) return false
    
    const userRoleLevel = ROLE_HIERARCHY[org.role] || 0
    const requiredRoleLevel = ROLE_HIERARCHY[requiredRole] || 0
    
    return userRoleLevel >= requiredRoleLevel
  }, [user, organizations])

  const value = useMemo(() => ({
    user,
    organizations,
    commandCenterAccess,
    authConfig,
    loading,
    error,
    login,
    logout,
    setupMFA,
    startMFAEnrollment,
    verifyMFAEnrollment,
    verifyMFA,
    forgotPassword,
    resetPassword,
    acceptInvitation,
    refreshProfile,
    hasRole,
    isAuthenticated: Boolean(user),
  }), [
    user,
    organizations,
    commandCenterAccess,
    authConfig,
    loading,
    error,
    login,
    logout,
    setupMFA,
    startMFAEnrollment,
    verifyMFAEnrollment,
    verifyMFA,
    forgotPassword,
    resetPassword,
    acceptInvitation,
    refreshProfile,
    hasRole,
  ])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
